/** Constantes compartilhadas entre API, worker e frontends. */

export const DEFAULT_TIMEZONE = 'America/Sao_Paulo';
export const DEFAULT_CURRENCY = 'BRL';
export const DEFAULT_LOCALE = 'pt-BR';

/** 0 = domingo, alinhado com Date#getDay() e com o campo weekday no banco. */
export const Weekday = {
  SUNDAY: 0,
  MONDAY: 1,
  TUESDAY: 2,
  WEDNESDAY: 3,
  THURSDAY: 4,
  FRIDAY: 5,
  SATURDAY: 6,
} as const;
export type Weekday = (typeof Weekday)[keyof typeof Weekday];

export const WEEKDAY_LABELS_PT: Readonly<Record<Weekday, string>> = {
  0: 'Domingo',
  1: 'Segunda-feira',
  2: 'Terça-feira',
  3: 'Quarta-feira',
  4: 'Quinta-feira',
  5: 'Sexta-feira',
  6: 'Sábado',
};

export const WEEKDAY_SHORT_PT: Readonly<Record<Weekday, string>> = {
  0: 'Dom',
  1: 'Seg',
  2: 'Ter',
  3: 'Qua',
  4: 'Qui',
  5: 'Sex',
  6: 'Sáb',
};

/**
 * Padrões de agenda aplicados a um tenant recém-criado.
 * Cada barbearia pode sobrescrever todos eles em TenantSettings.
 */
export const DEFAULT_BOOKING_SETTINGS = {
  /** Grade de horários oferecida ao cliente. */
  slotStepMinutes: 15,
  /** Antecedência mínima: não dá para agendar para daqui a 5 minutos. */
  bookingMinLeadMinutes: 60,
  /** Janela futura máxima de agendamento. */
  bookingMaxAdvanceDays: 60,
  /** Prazo para o cliente cancelar sem penalidade. */
  cancellationDeadlineHours: 4,
  allowCustomerReschedule: true,
  /** Agendamento nasce PENDING e exige confirmação da barbearia. */
  requireConfirmation: false,
  /** Horas antes do atendimento em que os lembretes são disparados. */
  reminderHours: [24, 2],
} as const;

/** Identidade visual neutra usada até a barbearia enviar a dela. */
export const DEFAULT_BRANDING = {
  primaryColor: '#111827',
  secondaryColor: '#D4AF37',
} as const;

/** Slug do tenant: usado em subdomínio e em /b/:slug, então segue regra de DNS. */
export const TENANT_SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,48}[a-z0-9])$/;

/**
 * Slugs reservados: não podem virar tenant porque colidiriam com subdomínios de
 * infraestrutura ou com rotas do próprio produto.
 */
export const RESERVED_TENANT_SLUGS: readonly string[] = [
  'admin',
  'api',
  'app',
  'auth',
  'blog',
  'cdn',
  'dashboard',
  'docs',
  'help',
  'mail',
  'painel',
  'plataforma',
  'static',
  'status',
  'suporte',
  'www',
];

export const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/** Paginação padrão de todas as listagens da API. */
export const PAGINATION = {
  defaultPage: 1,
  defaultPageSize: 20,
  maxPageSize: 100,
} as const;

/** Cabeçalho usado para correlacionar requisição, log e resposta de erro. */
export const REQUEST_ID_HEADER = 'x-request-id';

/**
 * Cabeçalho com o slug do tenant, enviado pelos frontends em rotas PÚBLICAS
 * (pré-login) quando não há subdomínio — típico de localhost.
 *
 * Atenção: em rotas AUTENTICADAS este header é ignorado por completo. O tenant
 * vem do JWT. Ele existe apenas para resolver branding e catálogo antes do login.
 */
export const TENANT_SLUG_HEADER = 'x-tenant-slug';
